import { ColumnFilter } from '@tanstack/react-table';

interface IBaseFilter extends ColumnFilter {
  name: string;
  onChange: unknown;
}

interface IDateFilter extends IBaseFilter {
  kind: 'date';
  value: {
    from: Date | null;
    to: Date | null;
  };
  onChange: {
    from: (value: Date | null) => void;
    to: (value: Date | null) => void;
  };
}

interface ITextFilter extends IBaseFilter {
  kind: 'text';
  value: string | null;
  onChange: (value: string | null) => void;
}

interface ISelectFilter extends IBaseFilter {
  kind: 'select';
  value: string | null;
  defaultValue: string | null;
  values: string[];
  onChange: (value: string | null) => void;
}

interface IMultiSelectFilter extends IBaseFilter {
  kind: 'multi-select';
  value: string[] | null;
  defaultValue: string[] | null;
  values: string[];
  onChange: (value: string[] | null) => void;
}

type FilterType = ITextFilter | IDateFilter | ISelectFilter | IMultiSelectFilter;

type StrictlyReadOnly<T> = {
  [K in keyof T as K extends 'onChange' ? never : K]: T[K];
};
type ReadOnlyTextFilter = StrictlyReadOnly<ITextFilter>;
type ReadOnlyDateFilter = StrictlyReadOnly<IDateFilter>;
type ReadOnlySelectFilter = StrictlyReadOnly<ISelectFilter>;
type ReadOnlyMultiSelectFilter = StrictlyReadOnly<IMultiSelectFilter>;
type ReadOnlyFilter = ReadOnlyTextFilter | ReadOnlyDateFilter | ReadOnlySelectFilter | ReadOnlyMultiSelectFilter;

const filterHasValue = (filter: FilterType): boolean => {
  switch (filter.kind) {
    case 'date':
      return Boolean(filter.value.from) || Boolean(filter.value.to);
    case 'text':
      return Boolean(filter.value);
    case 'select':
      return Boolean(filter.value);
    case 'multi-select':
      return Boolean(filter.value?.length);
    default: {
      ((_: never) => false)(filter);
      throw new Error('filterHasValue has not handled case ' + JSON.stringify(filter));
    }
  }
};

const formatFilterBadgeText = (filter: FilterType, badgeValue?: string): string => {
  switch (filter.kind) {
    case 'date':
      return dateBadgeText(filter);
    case 'text':
      return textBadgeText(filter);
    case 'select':
      return textBadgeText(filter);
    case 'multi-select':
      return multiSelectBadgeText(filter, badgeValue);
    default: {
      ((_: never) => false)(filter);
      throw new Error('formatFilterBadgeText has not handled case ' + JSON.stringify(filter));
    }
  }
};

const dateBadgeText = (filter: IDateFilter): string => {
  const fromDate = filter.value.from ? new Date(filter.value.from) : null;
  const toDate = filter.value.to ? new Date(filter.value.to) : null;

  if (fromDate && toDate) {
    return `${fromDate.toLocaleDateString()} - ${toDate.toLocaleDateString()}`;
  }

  if (fromDate) {
    return `From: ${fromDate.toLocaleDateString()}`;
  }

  if (toDate) {
    return `To: ${toDate.toLocaleDateString()}`;
  }

  return '';
};

const textBadgeText = (filter: ITextFilter | ISelectFilter): string => {
  return filter.value ?? '';
};

const multiSelectBadgeText = (filter: IMultiSelectFilter, badgeValue?: string): string => {
  if (badgeValue) return badgeValue;
  return filter.value?.join(', ') ?? '';
};

const clearFilter = (filter: FilterType, valueToClear?: string): void => {
  if (filter.kind === 'date') {
    filter.onChange.from(null);
    filter.onChange.to(null);
  } else if (filter.kind === 'text' || filter.kind === 'select') {
    filter.onChange(null);
  } else if (filter.kind === 'multi-select') {
    if (valueToClear) {
      const updatedValues = filter.value?.filter((value) => value !== valueToClear);
      filter.onChange(updatedValues?.length ? updatedValues : null);
      return;
    }

    filter.onChange(null);
  }
};

export { clearFilter, dateBadgeText, filterHasValue, formatFilterBadgeText, textBadgeText };

export type {
  FilterType,
  IBaseFilter,
  IDateFilter,
  IMultiSelectFilter,
  ISelectFilter,
  ITextFilter,
  ReadOnlyDateFilter,
  ReadOnlyFilter,
  ReadOnlyMultiSelectFilter,
  ReadOnlySelectFilter,
  ReadOnlyTextFilter,
};
